import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/lib/i18n/LanguageContext';

export interface SeoMeta {
  title: string;
  description: string | null;
  keywords: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  canonicalPath: string | null;
}

interface SeoPageMetaRow {
  id: string;
  page_key: string;
  language: string;
  title: string;
  description: string | null;
  keywords: string | null;
  og_title: string | null;
  og_description: string | null;
  canonical_path: string | null;
  is_auto_translated: boolean;
  needs_translation: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Hook to fetch SEO metadata from the database for a specific page and language.
 * Falls back to English if no translation exists for the current language.
 * 
 * @param pageKey - The page identifier (e.g., 'home', 'shop', 'product:state-id')
 * @returns SEO metadata for the page
 */
export function useSeoMeta(pageKey: string) {
  const { language } = useLanguage();

  return useQuery({
    queryKey: ['seo-meta', pageKey, language],
    queryFn: async (): Promise<SeoMeta | null> => {
      // Try to get SEO meta for current language
      const { data: localizedData, error: localizedError } = await supabase
        .from('seo_page_meta')
        .select('*')
        .eq('page_key', pageKey)
        .eq('language', language)
        .maybeSingle();

      if (localizedError) {
        console.error('Error fetching SEO meta:', localizedError);
      }

      // If found, return it
      if (localizedData) {
        const row = localizedData as SeoPageMetaRow;
        return {
          title: row.title,
          description: row.description,
          keywords: row.keywords,
          ogTitle: row.og_title,
          ogDescription: row.og_description,
          canonicalPath: row.canonical_path,
        };
      }

      // Fallback to English if no localized version exists
      if (language !== 'en') {
        const { data: englishData, error: englishError } = await supabase
          .from('seo_page_meta')
          .select('*')
          .eq('page_key', pageKey)
          .eq('language', 'en')
          .maybeSingle();

        if (englishError) {
          console.error('Error fetching English SEO meta fallback:', englishError);
        }

        if (englishData) {
          const row = englishData as SeoPageMetaRow;
          return {
            title: row.title,
            description: row.description,
            keywords: row.keywords,
            ogTitle: row.og_title,
            ogDescription: row.og_description,
            canonicalPath: row.canonical_path,
          };
        }
      }

      return null;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });
}

/**
 * Hook to fetch URL translation slugs for a specific page.
 * 
 * @param pageKey - The page identifier (e.g., 'shop', 'reviews')
 * @returns URL slugs for all languages
 */
export function useUrlTranslation(pageKey: string) {
  return useQuery({
    queryKey: ['url-translation', pageKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('url_translations')
        .select('*')
        .eq('page_key', pageKey)
        .maybeSingle();

      if (error) {
        console.error('Error fetching URL translation:', error);
        return null;
      }

      return data;
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour (URLs rarely change)
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
  });
}

/**
 * Hook to fetch all URL translations for generating hreflang links.
 * 
 * @returns All URL translations
 */
export function useAllUrlTranslations() {
  return useQuery({
    queryKey: ['url-translations-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('url_translations')
        .select('*');

      if (error) {
        console.error('Error fetching URL translations:', error);
        return [];
      }

      return data || [];
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
  });
}
